import {useForm} from 'react-hook-form'
import {useTranslation, TKey} from '@/translations/i18n.tsx'
import {InputText} from '@components/commons/input-text'
import {zodResolver} from '@hookform/resolvers/zod'
import {z} from 'zod'
import {
    ModalBody,
    ModalCloseButton,
    ModalFooter,
    ModalHeader,
    ModalHeaderTitle,
    ModalParagraph,
    ModalTitle
} from '@components/ui/modal-atoms'
import {Dialog, DialogClose, DialogContent} from '@components/ui/dialog'
import {Button} from '@components/ui/button'
import {Spinner} from '@components/ui/spinner'
import {useChangeFreelancerEmail} from '@features/settings/queries/useChangeFreelancerEmail'
import {toast} from '@utilities/toast'

const ValidationSchema = z.object({
    new_email: z.string().min(1, {message: 'errors:required'}).email({message: 'errors:email_invalid'}),
    password: z.string({required_error: 'errors:required'}).min(1, 'errors:required')
})

type ValidationSchema = z.infer<typeof ValidationSchema>

export const ChangeEmailModal = ({onClose}: {onClose: () => void}) => {
    const {t} = useTranslation()
    const onSuccess = () => {
        toast.success(
            <div className="text-sm [&_h3]:font-medium [&_p]:text-neutral-700">
                <h3>{t('settings:personal_information:verification_email_sent')}</h3>
                <p>{t('settings:personal_information:check_spam')}</p>
            </div>
        )
        onClose()
    }
    const changeFreelancerEmailMutation = useChangeFreelancerEmail({onSuccess})

    const {
        handleSubmit,
        register,
        formState: {errors, isValid}
    } = useForm<ValidationSchema>({
        mode: 'onChange',
        reValidateMode: 'onBlur',
        resolver: zodResolver(ValidationSchema)
    })

    const onSubmitHandler = handleSubmit(formValues => {
        changeFreelancerEmailMutation.mutate(formValues)
    })

    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent
                className="w-110 grid grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden"
                showCloseButton={false}
            >
                <ModalHeader className="grid grid-cols-[32px_1fr_32px] items-center justify-center border-none">
                    <DialogClose
                        data-slot="dialog-close"
                        render={<ModalCloseButton disabled={changeFreelancerEmailMutation.isPending} />}
                    />
                    <ModalHeaderTitle className="p-0">
                        {t('settings:personal_information:change_email')}
                    </ModalHeaderTitle>
                </ModalHeader>

                <ModalBody>
                    <div className="flex flex-col gap-2">
                        <ModalTitle>{t('settings:personal_information:change_email_title')}</ModalTitle>
                        <ModalParagraph>{t('settings:personal_information:change_email_description')}</ModalParagraph>
                    </div>
                    <InputText
                        label={t('settings:personal_information:new_email')}
                        placeholder={t('settings:personal_information:change_email_title')}
                        type="text"
                        errorMessage={t((errors.new_email?.message ?? '') as TKey)}
                        {...register('new_email')}
                    />
                    <InputText
                        label={t('commons:password')}
                        placeholder={t('settings:personal_information:current_password_placeholder')}
                        type="password"
                        errorMessage={t((errors.password?.message ?? '') as TKey)}
                        {...register('password')}
                        visibilityToggle
                    />
                </ModalBody>
                <ModalFooter>
                    <Button variant="tertiary" onClick={onClose} disabled={changeFreelancerEmailMutation.isPending}>
                        {t('commons:cancel')}
                        {changeFreelancerEmailMutation.isPending && <Spinner />}
                    </Button>
                    <Button onClick={onSubmitHandler} disabled={changeFreelancerEmailMutation.isPending || !isValid}>
                        {t('settings:personal_information:change_email')}
                        {changeFreelancerEmailMutation.isPending && <Spinner />}
                    </Button>
                </ModalFooter>
            </DialogContent>
        </Dialog>
    )
}
